// PricingSection.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SubscriptionService from '../../services/subscription.service';
import { authService } from '../../services/auth.service';
import './PricingSection.css';


const PricingSection = () => {
    const [isAnnual, setIsAnnual] = useState(true);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [loading, setLoading] = useState({
        initialLoad: true,
        plans: {}
    });
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadCurrentSubscription();
    }, []);

    const clearMessages = (delay = 5000) => {
        setTimeout(() => {
            setError(null);
            setSuccessMessage(null);
        }, delay);
    };

    const getActualPlanId = (baseId) => {
        if (baseId === 'free') return 'free';
        return isAnnual ? `${baseId}-yearly` : `${baseId}-monthly`;
    };

    const plans = [
        {
            baseId: 'free',
            name: "Gratuit",
            monthlyPrice: 0,
            yearlyPrice: 0,
            features: [
                "Accès aux fonctionnalités de base",
                "100 coins par mois",
                "jusqu'à 100 Mo / vidéo",
                "jsqu'à 1 minutes / vidéo",
                "Pas de carte bancaire requise"
            ],
            buttonText: currentPlan === 'free' ? "Plan actuel" : "Commencer gratuitement",
            buttonStyle: "secondary",
            popular: false
        },
        {
            baseId: 'classic',
            name: "Classique",
            monthlyPrice: 34.99,
            yearlyPrice: 24.99,
            features: [
                "Accès aux fonctionnalités de base",
                "Sans filigrane",
                "6000 coins par mois",
                "jusqu'à 1000 Mo / vidéo",
                "jusqu'à 10 minutes",
                "Support par email",
                isAnnual ? "Engagement 12 mois" : "Sans engagement",
            ].filter(Boolean),
            buttonText: currentPlan === getActualPlanId('classic') ? "Plan actuel" : "Commencer maintenant",
            buttonStyle: "secondary",
            popular: false
        },
        {
            baseId: 'pro',
            name: "Pro",
            monthlyPrice: 99.99,
            yearlyPrice: 74.99,
            features: [
                "Toutes les fonctionnalités Classic",
                "Sans filigrane",
                "30000 coins par mois",
                "jusqu'à 2000 Mo / vidéo",
                "jusqu'à 30 minutes",
                "Support prioritaire",
                isAnnual ? "Engagement 12 mois" : "Sans engagement",
            ].filter(Boolean),
            buttonText: currentPlan === getActualPlanId('pro') ? "Plan actuel" : "Commencer maintenant",
            buttonStyle: "primary",
            popular: true
        },
        {
            baseId: 'enterprise',
            name: "Entreprise",
            price: "Sur mesure",
            features: [
                "Solution personnalisée",
                "Accompagnement dédié",
                "Support prioritaire 24/7",
                "Fonctionnalités sur mesure"
            ],
            buttonText: "Contacter nous",
            buttonStyle: "secondary",
            popular: false
        }
    ];

    const loadCurrentSubscription = async () => {
        const hasUsername = localStorage.getItem('username');
        if (!hasUsername) {
            setLoading(prev => ({ ...prev, initialLoad: false }));
            setCurrentPlan(null);
            return;
        }

        try {
            const subscription = await SubscriptionService.getCurrentSubscription();
            if (subscription) {
                setCurrentPlan(subscription.planId);
            }
        } catch (error) {
            console.error('Error loading subscription:', error);
        } finally {
            setLoading(prev => ({ ...prev, initialLoad: false }));
        }
    };

    const handleSubscribe = async (baseId) => {
        if (baseId === 'enterprise') {
            window.location.href = '/demo';
            return;
        }

        if (!authService.isAuthenticated()) {
            navigate('/login', { state: { from: '/pricing' } });
            return;
        }

        const actualPlanId = getActualPlanId(baseId);

        try {
            setLoading(prev => ({
                ...prev,
                plans: {
                    ...prev.plans,
                    [baseId]: true
                }
            }));
            setError(null);
            setSuccessMessage(null);

            if (baseId === 'free') {
                const result = await SubscriptionService.subscribeToFreePlan();
                if (result.message) {
                    setSuccessMessage(result.message);
                    clearMessages(8000);
                }
                if (result.redirect) {
                    setTimeout(() => {
                        window.location.href = result.redirect;
                    }, 2000);
                }
                return;
            }

            const subscription = await SubscriptionService.getCurrentSubscription();

            if (subscription.status !== 'none') {
                if (subscription.planId === actualPlanId) {
                    setError("Vous avez déjà souscrit à cet abonnement");
                    clearMessages();
                    return;
                }

                const result = await SubscriptionService.updateSubscription(actualPlanId);
                if (result && result.message) {
                    setSuccessMessage(result.message);
                    clearMessages(8000);
                    if (result.redirect) {
                        setTimeout(() => {
                            window.location.href = result.redirect;
                        }, 2000);
                    }
                }
            } else {
                const result = await SubscriptionService.subscribe(actualPlanId);
                if (result && result.message) {
                    setSuccessMessage(result.message);
                    clearMessages(8000);
                    if (result.redirect) {
                        setTimeout(() => {
                            window.location.href = result.redirect;
                        }, 2000);
                    }
                }
            }

            await loadCurrentSubscription();
        } catch (error) {
            setError(error.message || "Erreur lors de la souscription");
            clearMessages();
        } finally {
            setLoading(prev => ({
                ...prev,
                plans: {
                    ...prev.plans,
                    [baseId]: false
                }
            }));
        }
    };

    return (
        <div className="pricing-section">
            <div className="pricing-section__header">
                <h1 className="pricing-section__title">Des forfaits adaptés à vos besoins</h1>
                <p className="pricing-section__subtitle">
                    Choisissez le plan qui correspond le mieux à votre utilisation et développez votre audience internationale
                </p>

                {error && (
                    <div className="pricing-section__error">
                        <i className="fas fa-exclamation-circle"></i>
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div className="pricing-section__success">
                        <i className="fas fa-check-circle"></i>
                        {successMessage}
                    </div>
                )}

                <div className="pricing-section__toggle">
                    <button
                        className={`pricing-section__toggle-button ${!isAnnual ? 'pricing-section__toggle-button--active' : ''}`}
                        onClick={() => setIsAnnual(false)}
                    >
                        Mensuel
                    </button>
                    <button
                        className={`pricing-section__toggle-button ${isAnnual ? 'pricing-section__toggle-button--active' : ''}`}
                        onClick={() => setIsAnnual(true)}
                    >
                        Annuel (-20%)
                    </button>
                </div>
            </div>

            <div className="pricing-section__grid">
                {plans.map((plan, index) => (
                    <div
                        key={index}
                        className={`pricing-section__card ${plan.popular ? 'pricing-section__card--popular' : ''}`}
                    >
                        {plan.popular && (
                            <div className="pricing-section__popular-badge">Plus populaire</div>
                        )}
                        <h3 className="pricing-section__card-title">{plan.name}</h3>
                        <div className={`pricing-section__price ${plan.baseId === 'enterprise' ? 'pricing-section__price--custom' : ''}`}>
                            {plan.price === "Sur mesure" ? (
                                plan.price
                            ) : (
                                <>
                                    {isAnnual ? plan.yearlyPrice : plan.monthlyPrice}€ <span>/mois</span>
                                </>
                            )}
                        </div>
                        <ul className="pricing-section__features">
                            {plan.features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="pricing-section__feature">
                                    <i className="fas fa-check"></i>
                                    {feature}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() => handleSubscribe(plan.baseId)}
                            disabled={loading.plans[plan.baseId] || (currentPlan === getActualPlanId(plan.baseId))}
                            className={`
                                pricing-section__button 
                                pricing-section__button--${plan.buttonStyle}
                                ${loading.plans[plan.baseId] ? 'pricing-section__button--loading' : ''}
                            `}
                        >
                            {plan.baseId === 'enterprise'
                                ? "Contacter nous"
                                : loading.plans[plan.baseId]
                                    ? 'Chargement...'
                                    : currentPlan === getActualPlanId(plan.baseId)
                                        ? "Abonnement actuel"
                                        : currentPlan
                                            ? "Changer pour ce plan"
                                            : "Commencer maintenant"
                            }
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PricingSection;